// @ts-nocheck
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, ExternalLink, ArrowLeft } from 'lucide-react';
import { useEbayTemplates, type EbayTemplate } from '@/hooks/useEbayTemplates';
import { goldGradientActive } from '@/lib/gradientStyles';
import { cn } from '@/lib/utils';

const VARIABLE_HINTS = [
	'{{title}}', '{{game}}', '{{set}}', '{{set_code}}', '{{card_number}}',
	'{{rarity}}', '{{condition}}', '{{finish}}', '{{language}}',
	'{{grade}}', '{{grading_company}}', '{{seller_username}}'
];

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function EbayTemplatesModal({ open, onOpenChange }: Props) {
	const { list, save, remove } = useEbayTemplates();
	const [editing, setEditing] = useState<Partial<EbayTemplate> | null>(null);

	const presets = (list.data || []).filter((t) => t.scope === 'preset');
	const mine = (list.data || []).filter((t) => t.scope === 'user');

	const handleSave = async () => {
		if (!editing?.name?.trim()) return;
		await save.mutateAsync({
			id: editing.id,
			name: editing.name.trim(),
			description: editing.description || null,
			body_html: editing.body_html || null,
			wrapper_html: editing.wrapper_html || null,
			applies_to_game: editing.applies_to_game || null,
			applies_to_product_type: editing.applies_to_product_type || null
		});
		setEditing(null);
	};

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent variant="fullscreen" className="sm:max-w-2xl sm:max-h-[88dvh] overflow-y-auto">
					<DialogHeader>
						<div className="flex items-center gap-2 sm:hidden -ml-2 -mt-1 mb-1">
							<button
								type="button"
								onClick={() => onOpenChange(false)}
								aria-label="Close"
								className="h-10 w-10 inline-flex items-center justify-center rounded-full text-foreground/80 hover:text-foreground hover:bg-white/[0.06]"
							>
								<ArrowLeft className="w-5 h-5" />
							</button>
						</div>
						<DialogTitle>eBay Description Templates</DialogTitle>
						<DialogDescription>
							Reusable HTML for the body of your eBay listings. Use{' '}
							<code className="text-active-gold">{'{{title}}'}</code>,{' '}
							<code className="text-active-gold">{'{{set}}'}</code>, and other variables — they fill in per item.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-6">
						{/* Presets */}
						<section className="space-y-3">
							<div>
								<h3 className="text-xs uppercase tracking-wide text-muted-foreground">CardSimp presets</h3>
								<p className="text-xs text-muted-foreground/80 mt-0.5">Built-in templates. Duplicate one to customize it.</p>
							</div>
							{list.isLoading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : presets.length === 0 ? (
								<p className="text-xs text-muted-foreground">No presets yet.</p>
							) : (
								<div className="space-y-1.5">
									{presets.map((t) => (
										<div key={t.id} className="flex items-center justify-between gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2">
											<div className="min-w-0">
												<div className="text-sm font-medium truncate">{t.name}</div>
												{t.description && <div className="text-xs text-muted-foreground truncate">{t.description}</div>}
											</div>
											<Button
												type="button" variant="outline" size="sm" className="bg-white/[0.05] shrink-0"
												onClick={() => setEditing({
													name: `${t.name} (copy)`,
													description: t.description,
													body_html: t.body_html,
													wrapper_html: t.wrapper_html,
													applies_to_game: t.applies_to_game,
													applies_to_product_type: t.applies_to_product_type
												})}
											>Duplicate</Button>
										</div>
									))}
								</div>
							)}
						</section>

						<div className="border-t border-white/[0.06]" />

						{/* Your templates */}
						<section className="space-y-3">
							<div className="flex items-start justify-between gap-3">
								<div>
									<h3 className="text-xs uppercase tracking-wide text-muted-foreground">Your templates</h3>
									<p className="text-xs text-muted-foreground/80 mt-0.5">Custom HTML you've saved. Pick one when listing an item.</p>
								</div>
								<Button
									type="button" size="sm"
									className={cn(goldGradientActive, 'shrink-0')}
									onClick={() => setEditing({ name: '', body_html: '<p>Beautiful card from a smoke-free home. Ships in a top-loader + bubble mailer.</p>' })}
								>
									<Plus className="h-4 w-4 mr-1" /> New template
								</Button>
							</div>
							{mine.length === 0 ? (
								<p className="text-xs text-muted-foreground">No saved templates yet.</p>
							) : (
								<div className="space-y-1.5">
									{mine.map((t) => (
										<div key={t.id} className="flex items-center justify-between gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2">
											<div className="min-w-0">
												<div className="text-sm font-medium truncate">{t.name}</div>
												{t.description && <div className="text-xs text-muted-foreground truncate">{t.description}</div>}
											</div>
											<div className="flex items-center gap-1 shrink-0">
												<Button type="button" variant="ghost" size="icon" aria-label={`Edit template ${t.name}`} className="h-8 w-8 rounded-full" onClick={() => setEditing(t)}>
													<Pencil className="h-3.5 w-3.5" />
												</Button>
												<AlertDialog>
													<AlertDialogTrigger asChild>
														<Button type="button" variant="ghost" size="icon" aria-label={`Delete template ${t.name}`} className="h-8 w-8 rounded-full text-destructive/80 hover:text-destructive">
															<Trash2 className="h-3.5 w-3.5" />
														</Button>
													</AlertDialogTrigger>
													<AlertDialogContent>
														<AlertDialogHeader>
															<AlertDialogTitle>Delete "{t.name}"?</AlertDialogTitle>
															<AlertDialogDescription>Items using this template will fall back to the default CardSimp body.</AlertDialogDescription>
														</AlertDialogHeader>
														<AlertDialogFooter>
															<AlertDialogCancel>Cancel</AlertDialogCancel>
															<AlertDialogAction onClick={() => remove.mutate(t.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
														</AlertDialogFooter>
													</AlertDialogContent>
												</AlertDialog>
											</div>
										</div>
									))}
								</div>
							)}
						</section>

						<p className="text-[11px] text-muted-foreground flex items-center gap-1.5 pt-1">
							<ExternalLink className="h-3 w-3 shrink-0" />
							Scripts, iframes, forms, and on* handlers are stripped automatically. Tables and inline styles are kept.
						</p>
					</div>
				</DialogContent>
			</Dialog>

			{/* Editor dialog (nested) */}
			<Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
				<DialogContent variant="fullscreen" className="sm:max-w-2xl sm:max-h-[88dvh] overflow-y-auto">
					<DialogHeader>
						<div className="flex items-center gap-2 sm:hidden -ml-2 -mt-1 mb-1">
							<button
								type="button"
								onClick={() => setEditing(null)}
								aria-label="Close"
								className="h-10 w-10 inline-flex items-center justify-center rounded-full text-foreground/80 hover:text-foreground hover:bg-white/[0.06]"
							>
								<ArrowLeft className="w-5 h-5" />
							</button>
						</div>
						<DialogTitle>{editing?.id ? 'Edit template' : 'New template'}</DialogTitle>
					</DialogHeader>
					{editing && (
						<div className="space-y-4">
							<div className="space-y-1">
								<Label className="text-xs">Name</Label>
								<Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="My slab pitch" />
							</div>
							<div className="space-y-1">
								<Label className="text-xs">Description (optional, for your own reference)</Label>
								<Input value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Used for PSA slabs" />
							</div>
							<div className="grid grid-cols-2 gap-3">
								<div className="space-y-1">
									<Label className="text-xs">Auto-apply to game (optional)</Label>
									<Input value={editing.applies_to_game || ''} onChange={(e) => setEditing({ ...editing, applies_to_game: e.target.value })} placeholder="pokemon, mtg, …" />
								</div>
								<div className="space-y-1">
									<Label className="text-xs">Auto-apply to product type (optional)</Label>
									<Input value={editing.applies_to_product_type || ''} onChange={(e) => setEditing({ ...editing, applies_to_product_type: e.target.value })} placeholder="slab, sealed, single, …" />
								</div>
							</div>
							<div className="space-y-1">
								<Label className="text-xs">Body HTML</Label>
								<Textarea
									value={editing.body_html || ''}
									onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
									rows={10}
									className="font-mono text-xs"
									placeholder={'<p>{{title}} — {{set}} #{{card_number}}</p>\n<ul>\n  <li>Condition: {{condition}}</li>\n  <li>Ships in top-loader + bubble mailer</li>\n</ul>'}
								/>
								<div className="text-[10px] text-muted-foreground">
									Variables: {VARIABLE_HINTS.map((v) => <code key={v} className="text-active-gold mr-2">{v}</code>)}
								</div>
							</div>
							<div className="space-y-1">
								<Label className="text-xs">Wrapper HTML (optional)</Label>
								<Textarea
									value={editing.wrapper_html || ''}
									onChange={(e) => setEditing({ ...editing, wrapper_html: e.target.value })}
									rows={5}
									className="font-mono text-xs"
									placeholder="Leave blank to use the default CardSimp wrapper. Use {{body}} to mark where the body goes."
								/>
								<p className="text-[10px] text-muted-foreground">Advanced — replace the header/footer/specs block. Must include {'{{body}}'}.</p>
							</div>
						</div>
					)}
					<DialogFooter>
						<Button variant="outline" className="bg-white/[0.05]" onClick={() => setEditing(null)}>Cancel</Button>
						<Button
							className={goldGradientActive}
							onClick={handleSave}
							disabled={save.isPending || !editing?.name?.trim()}
						>
							{save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save template'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
